import { useEffect, useState } from "react";
import { ArrowLeft, User, Gamepad2, Briefcase, Coins, Building2, ChevronRight, LayoutGrid, Landmark } from "lucide-react";
import { useSession, TIER_META } from "@/context/SessionContext";
import ProfileSettingsMatrix from "@/components/dashboard/ProfileSettingsMatrix";
import WealthRewardsPanel from "@/components/dashboard/WealthRewardsPanel";
import WealthMonitor from "@/pages/WealthMonitor";
import BankBeatEngine from "@/components/wealth/BankBeatEngine";
import BusinessPlanInterface from "@/components/dashboard/BusinessPlanInterface";
import WalletMonetizationHub from "@/components/dashboard/WalletMonetizationHub";
import VanDesignWorkspace from "@/components/vandesign/VanDesignWorkspace";
import AdMakerWorkspace from "@/components/mist/AdMakerWorkspace";
import MarketingWorkspace from "@/components/workspaces/MarketingWorkspace";
import { HUB_DETAIL } from "@/components/dashboard/HubDetail";

// STAGE 6 · PHASE 2 / STAGE 8 — Misteck Life hub (client-side layout wrapper).
// Opens to a premium `overview` LAUNCHER landing (parity with MisteckPlayHub),
// NOT the Profile settings form. Profile is demoted to a normal destination tab.
// 5 destination areas + a launcher overview. No backend of its own.

const TABS = [
  { key: "profile", label: "Profile", Icon: User, color: "#E2D4F8", sub: "Identity · Settings · Security" },
  { key: "leisure", label: "Leisure", Icon: Gamepad2, hub: "lifestyle_hub", color: "#FF9E9E", sub: "Lifestyle · Travel · Fun" },
  { key: "career", label: "Career", Icon: Briefcase, hub: "career_hub", color: "#FFD700", sub: "Jobs · CV · Autopilot" },
  { key: "wealth", label: "Wealth", Icon: Coins, hub: "wealth_hub", color: "#98FFB6", sub: "Money · Wallet · Rewards" },
  { key: "business", label: "Business", Icon: Building2, hub: "business_hub", color: "#A4DCFA", sub: "Plans · Tools · Growth" },
];
const VALID = TABS.map((t) => t.key);

// Premium launcher landing — large flat brutalist cards routing into each area.
function OverviewLanding({ onPick, badges }) {
  return (
    <div className="space-y-3.5" data-testid="life-overview">
      <p className="px-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">Choose an area</p>
      {TABS.map((t) => (
        <button
          key={t.key}
          onClick={() => onPick(t.key)}
          data-testid={`life-overview-${t.key}`}
          className="w-full min-h-[15vh] flex items-center gap-4 border-2 border-black rounded-[22px] px-4 py-5 text-left shadow-[5px_5px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform"
          style={{ backgroundColor: t.color }}
        >
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border-2 border-black bg-white/40">
            <t.Icon size={26} strokeWidth={2.4} className="text-black" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-3xl leading-none text-black">{t.label}</p>
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-black/60 truncate">{t.sub}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {badges?.[t.key] && (
              <span data-testid={`life-badge-${t.key}`} className="max-w-[92px] truncate rounded-lg border-2 border-black bg-black px-2 py-1 font-mono text-[10px] font-black uppercase tracking-wide text-white">
                {badges[t.key]}
              </span>
            )}
            <ChevronRight size={22} className="text-black/50" />
          </div>
        </button>
      ))}
    </div>
  );
}

function OptionList({ hubKey, onOpenRow }) {
  const detail = HUB_DETAIL[hubKey] || { rows: [], accent: "#94A3B8" };
  const accent = detail.accent || "#94A3B8";
  return (
    <div className="rounded-3xl border-2 border-black bg-cream p-3 shadow-[4px_4px_0px_#000]" data-testid={`life-options-${hubKey}`}>
      {detail.subtitle && (
        <p className="px-1 pb-2 font-mono text-[9px] uppercase tracking-[0.18em] text-charcoal/50">{detail.subtitle}</p>
      )}
      <div className="space-y-2.5">
        {detail.rows.map((r, i) => {
          const Icon = r.Icon;
          return (
            <button
              key={`${r.t}-${i}`}
              onClick={() => onOpenRow?.(r)}
              data-testid={`life-option-${hubKey}-${i}`}
              className="w-full flex items-center gap-3 border-2 border-charcoal rounded-2xl bg-white px-3.5 py-3 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform text-left"
            >
              <span className="w-11 h-11 shrink-0 rounded-xl border-2 border-charcoal grid place-items-center" style={{ backgroundColor: r.color || accent }}>
                <Icon size={20} strokeWidth={2.3} className="text-white" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-bold text-[15px] text-charcoal leading-tight">{r.t}</p>
                <p className="font-mono text-[10px] text-charcoal/55 leading-snug mt-0.5">{r.d}</p>
              </div>
              <ChevronRight size={20} className="text-charcoal/40 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MisteckLifeHub({ onBack, onOpenDesk, onNavigate, initialTab }) {
  // Default entry = overview launcher. A valid ?life= deep-link opens that tab.
  const [tab, setTab] = useState(initialTab && VALID.includes(initialTab) ? initialTab : "overview");
  const [bizOpen, setBizOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [mortgageOpen, setMortgageOpen] = useState(false);
  const [vanOpen, setVanOpen] = useState(false);
  const [admakerOpen, setAdmakerOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const { wallet, tier } = useSession();
  const active = TABS.find((t) => t.key === tab);
  const isOverview = tab === "overview";

  // Suppress the FOUNDER_MODE_TEST_FEED PiP while inside the Life hub so it
  // never obstructs the launcher cards; restore deterministically on exit.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);

  // Live telemetry badges — sourced ONLY from already-hydrated client state
  // (session wallet + tier). No new backend. Neutral labels where no metric.
  const bal = wallet?.balance;
  const badges = {
    profile: (TIER_META[tier] && TIER_META[tier].label) || "MEMBER",
    wealth: bal != null ? `${Number(bal).toLocaleString()} M` : "WALLET",
    business: "TOOLS",
    career: "AUTOPILOT",
    leisure: "EXPLORE",
  };

  // Honour a ?life= deep-link that arrives AFTER mount (CommandStackS6 consumes
  // ?zone=life synchronously, so initialTab can update once the effect runs).
  useEffect(() => {
    if (initialTab && VALID.includes(initialTab)) setTab(initialTab);
  }, [initialTab]);

  // Context-aware back: overview → exit zone; any area tab → back to overview.
  const goBack = () => (isOverview ? onBack?.() : setTab("overview"));

  // Launch the area's native desk WHILE preserving Life-zone context, so
  // back/collapse from the desk returns the user into this exact Life tab.
  const openDesk = () => { if (active?.hub) onOpenDesk?.(active.hub, tab); };

  const openRow = (r) => {
    if (r?.page === "marketing") { setMarketingOpen(true); return; }
    if (r?.page === "vandesign") { setVanOpen(true); return; }
    if (r?.page === "admaker") { setAdmakerOpen(true); return; }
    if (r?.routeHub) { onOpenDesk?.(r.routeHub, tab); return; }
    openDesk();
  };

  const headerTitle = isOverview ? "Life Overview" : (tab === "profile" ? "Profile" : active.label);

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-slate-950" data-testid="misteck-life-hub">
      {/* Header — brutalist bold separator */}
      <div className="flex items-center gap-2 border-b-2 border-white/70 px-4 py-3">
        <button onClick={goBack} data-testid="life-hub-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-white/5 active:scale-95">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-slate-400">Misteck Life</p>
          <h2 className="font-serif text-xl text-white">{headerTitle}</h2>
        </div>
      </div>

      {/* Tab pills — Overview + the 5 areas */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar border-b-2 border-white/70 px-3 py-2.5" data-testid="life-hub-tabs">
        <button
          onClick={() => setTab("overview")}
          data-testid="life-tab-overview"
          className={[
            "flex shrink-0 items-center gap-1.5 rounded-full border-2 px-3 py-1.5 font-mono text-[10px] font-black uppercase tracking-wide transition-colors",
            isOverview ? "border-white bg-white text-slate-950" : "border-white/40 bg-white/[0.03] text-white/60",
          ].join(" ")}
        >
          <LayoutGrid size={13} /> Overview
        </button>
        {TABS.map((t) => {
          const on = tab === t.key;
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`life-tab-${t.key}`}
              className={[
                "flex shrink-0 items-center gap-1.5 rounded-full border-2 px-3 py-1.5 font-mono text-[10px] font-black uppercase tracking-wide transition-colors",
                on ? "border-white bg-white text-slate-950" : "border-white/40 bg-white/[0.03] text-white/60",
              ].join(" ")}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto no-scrollbar p-4 pb-28" data-testid={`life-panel-${tab}`}>
        {isOverview ? (
          <OverviewLanding onPick={setTab} badges={badges} />
        ) : tab === "profile" ? (
          <ProfileSettingsMatrix />
        ) : (
          <>
            {tab === "wealth" && (
              <>
                <button
                  onClick={() => setWalletOpen(true)}
                  data-testid="open-wallet-hub"
                  className="mb-3 w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#FFCC00] text-black px-4 py-3.5 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
                >
                  <Coins size={15} /> Wallet & Monetization Hub <ChevronRight size={15} />
                </button>
                <button
                  onClick={() => setMortgageOpen(true)}
                  data-testid="open-mortgage-blueprint"
                  className="mb-3 w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#98FFB6] text-black px-4 py-3.5 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
                >
                  <Landmark size={15} /> Mortgage Blueprint <ChevronRight size={15} />
                </button>
                <WealthMonitor />
                <div className="my-4 h-0.5 w-full bg-black/10" />
                <WealthRewardsPanel onOpenBusinessPlan={() => setBizOpen(true)} />
              </>
            )}
            <button
              onClick={openDesk}
              data-testid={`life-open-desk-${active.hub}`}
              className="mb-3 w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-black bg-white text-slate-950 px-4 py-3.5 font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
            >
              <active.Icon size={15} /> Open {active.label} Desk <ChevronRight size={15} />
            </button>
            {/* Leisure de-dup: the Leisure desk IS the option list — no inline copy. */}
            {tab !== "leisure" && <OptionList hubKey={active.hub} onOpenRow={openRow} />}
          </>
        )}
      </div>

      {bizOpen && <BusinessPlanInterface onBack={() => setBizOpen(false)} />}
      {walletOpen && <WalletMonetizationHub onBack={() => setWalletOpen(false)} />}
      {mortgageOpen && (
        <div className="absolute inset-0 z-50 flex flex-col bg-slate-950" data-testid="mortgage-workspace">
          <div className="flex items-center gap-2 border-b-2 border-white/70 px-4 py-3">
            <button onClick={() => setMortgageOpen(false)} data-testid="mortgage-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-white/5 active:scale-95">
              <ArrowLeft size={16} className="text-white" />
            </button>
            <div className="leading-tight">
              <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-slate-400">Wealth · Mortgage</p>
              <h2 className="font-serif text-xl text-white">Mortgage Blueprint</h2>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar p-4 pb-28" data-testid="mortgage-panel">
            <BankBeatEngine />
          </div>
        </div>
      )}
      {vanOpen && <VanDesignWorkspace onBack={() => setVanOpen(false)} />}
      {admakerOpen && <AdMakerWorkspace onBack={() => setAdmakerOpen(false)} />}
      {marketingOpen && (
        <div className="absolute inset-0 z-50" data-testid="marketing-overlay">
          <MarketingWorkspace onClose={() => setMarketingOpen(false)} />
        </div>
      )}
    </div>
  );
}
